import { HttpStatusCode } from '@angular/common/http';
import { computed, effect, inject, Injectable, Injector, signal } from '@angular/core';

import { Avatar, BusinessProfile, UserGoogleProfile } from '@zeusm/shared/core/entities';
import { ProfileType } from '@zeusm/shared/core/enums';
import { ThemeService } from '@zeusm/shared/core/services';
import { extractUserRoles, hasSupportManagerRole, UserStore } from '@zeusm/shared/session/user';

import { AuthStore } from '../../auth/auth.store';
import { ProfileSwitchContext } from '../models';
import { ProfileSwitchService } from './profile-switch.service';

const TREASURER_AVATAR: Avatar = {
  smallUrl: '/assets/images/treasurer.webp',
  bigUrl: '/assets/images/treasurer.webp',
};
const BUSINESS_AVATAR: Avatar = {
  smallUrl: '/assets/images/business-light.svg',
  bigUrl: '/assets/images/business-light.svg',
};
const SUPPORT_LIGHT_AVATAR: Avatar = {
  smallUrl: '/assets/images/support-manager-light.svg',
  bigUrl: '/assets/images/support-manager-light.svg',
};
const SUPPORT_DARK_AVATAR: Avatar = {
  smallUrl: '/assets/images/support-manager-dark.svg',
  bigUrl: '/assets/images/support-manager-dark.svg',
};

type SetActiveProfileOptions = {
  navigateToRoot?: boolean;
};

@Injectable({ providedIn: 'root' })
export class ProfileService {
  readonly #authStore = inject(AuthStore);
  readonly #injector = inject(Injector);
  readonly #profileSwitchService = inject(ProfileSwitchService);
  readonly #themeService = inject(ThemeService);
  #userStore: InstanceType<typeof UserStore> | null = null;

  readonly #profile = signal<UserGoogleProfile | null>(null);
  readonly #initialized = signal<boolean>(false);
  readonly #getMeFailed = signal<boolean>(false);
  readonly #profileSwitchContext = computed<ProfileSwitchContext>(() => {
    const profile = this.#profile();
    const user = this.#user.user();

    return {
      hasPersonalProfileAccess: this.#hasPersonalProfileAccess(),
      hasSupportProfileAccess: hasSupportManagerRole(user?.roles),
      isSuperUser: profile?.role?.includes('superuser') ?? false,
      businessProfiles: user?.businessProfiles ?? [],
    };
  });

  readonly me = computed(() => this.#user.user());
  readonly hasPersonalProfileAccess = computed(() => this.#hasPersonalProfileAccess());
  readonly hasSupportProfileAccess = computed(() => hasSupportManagerRole(this.me()?.roles));
  readonly businessProfiles = computed(() => this.me()?.businessProfiles ?? []);

  readonly isSuperUser = computed(() => {
    const profile = this.#profile();
    return profile?.role?.includes('superuser') ?? false;
  });
  readonly isOwner = computed(() => {
    const profile = this.#profile();
    return profile?.role?.includes('owner') ?? false;
  });

  readonly selectedBusinessProfile = computed<BusinessProfile | null>(() => {
    return this.#profileSwitchService.resolveSelectedBusinessProfile(this.#profileSwitchContext());
  });
  readonly isSelectedBusinessProfileLiquidity = computed(() => !!this.selectedBusinessProfile()?.isLiquidity);

  readonly activeProfile = this.#profileSwitchService.activeProfile;
  readonly profileChange = this.#profileSwitchService.profileChange;

  readonly isActiveTreasurer = computed(() => {
    return this.activeProfile() === ProfileType.TREASURER;
  });
  readonly isSupportOnly = this.hasSupportProfileAccess;

  readonly isBusinessProfileActive = computed(() => this.activeProfile() === ProfileType.BUSINESS && !!this.selectedBusinessProfile()?.id);

  readonly displayAvatar = computed<Avatar>(() => {
    const activeProfile = this.activeProfile();

    if (activeProfile === ProfileType.TREASURER) return TREASURER_AVATAR;
    if (activeProfile === ProfileType.BUSINESS) return BUSINESS_AVATAR;
    if (activeProfile === ProfileType.SUPPORT) return this.supportAvatar;

    return this.#profile()?.avatar;
  });

  readonly displayCaption = computed<string>(() => {
    const activeProfile = this.activeProfile();

    if (activeProfile === ProfileType.TREASURER) return 'common.profile.treasurer';
    if (activeProfile === ProfileType.SUPPORT) return 'common.profile.supportManagement';
    if (activeProfile === ProfileType.BUSINESS) return this.selectedBusinessProfile()?.name ?? '';

    return this.#profile()?.caption;
  });

  readonly profile = this.#profile.asReadonly();
  readonly isProfileLoaded = computed(() => this.#profile() !== null);

  readonly treasurerAvatar: Avatar = TREASURER_AVATAR;
  readonly businessAvatar: Avatar = BUSINESS_AVATAR;

  readonly isAuthorizedEffect = effect(() => {
    const isAuthorized = this.#authStore.isAuthorized();
    const hasUser = !!this.#user.user();
    const isGetMePending = this.#user.isGetMePending();
    const getMeFailed = this.#getMeFailed();

    if (isAuthorized) {
      if (!hasUser && !isGetMePending && !getMeFailed) {
        this.#user.getMe();
      }
    } else {
      this.#clearAll();
    }
  });

  /** Effect to monitor user data and update profile */
  readonly userDataEffect = effect(() => {
    const user = this.#user.user();
    const isAuthorized = this.#authStore.isAuthorized();

    if (isAuthorized && user) {
      this.#profile.set(this.#buildProfile());
      this.#profileSwitchService.initializeActiveProfile(this.#profileSwitchContext());
    }
  });

  /** Effect to monitor getMe and update auth status */
  readonly getMeEffect = effect(() => {
    const getMeSuccess = this.#user.isGetMeSuccess();
    const getMeError = this.#user.getMeError();

    if (getMeSuccess !== null) {
      const me = this.#user.user();
      const isValid = !!me?.id;
      this.#authStore.setAuthStatus(isValid);

      if (isValid) {
        this.#getMeFailed.set(false);
      } else {
        this.#getMeFailed.set(true);
      }
    }

    if (getMeError) {
      const errorStatus = getMeError.originalError?.status;
      if (
        errorStatus === HttpStatusCode.BadRequest ||
        errorStatus === HttpStatusCode.Unauthorized ||
        errorStatus === HttpStatusCode.Forbidden
      ) {
        this.#getMeFailed.set(true);
        this.#authStore.setAuthStatus(false);
      }
    }
  });

  get supportAvatar(): Avatar {
    return this.#themeService.isDarkTheme() ? SUPPORT_DARK_AVATAR : SUPPORT_LIGHT_AVATAR;
  }

  get #user(): InstanceType<typeof UserStore> {
    if (!this.#userStore) {
      this.#userStore = this.#injector.get(UserStore);
    }
    return this.#userStore;
  }

  /** Initialize authentication by fetching user profile */
  initializeAuth(): void {
    const hasUser = !!this.#user.user();
    const isGetMePending = this.#user.isGetMePending();
    const getMeFailed = this.#getMeFailed();

    if (hasUser || isGetMePending || getMeFailed || this.#initialized()) return;

    this.#initialized.set(true);
    this.#user.getMe();
  }

  /** Set active profile and persist to storage */
  setActiveProfile(profile: ProfileType, options: SetActiveProfileOptions = {}): void {
    this.#profileSwitchService.setActiveProfile(profile, this.#profileSwitchContext(), options);
  }

  setActiveBusinessProfile(profileId: string): void {
    this.#profileSwitchService.setActiveBusinessProfile(profileId, this.#profileSwitchContext());
  }

  ensurePersonalProfileActive(): boolean {
    return this.#profileSwitchService.ensurePersonalProfileActive(this.#profileSwitchContext());
  }

  ensureBusinessProfileActive(profileId?: string | null): boolean {
    return this.#profileSwitchService.ensureBusinessProfileActive(this.#profileSwitchContext(), profileId);
  }

  ensureSupportProfileActive(): boolean {
    return this.#profileSwitchService.ensureSupportProfileActive(this.#profileSwitchContext());
  }

  #buildProfile(): UserGoogleProfile | null {
    const meData = this.me();
    if (!meData) return null;

    const { firstName, lastName, email, avatar, roles, termsAccepted } = meData;
    const role = extractUserRoles(roles);
    return {
      name: firstName ?? '',
      surname: lastName ?? '',
      caption: `${firstName ?? ''} ${lastName ?? ''}`.trim(),
      role,
      avatar: {
        smallUrl: avatar ?? '',
        bigUrl: avatar ?? '',
      } as Avatar,
      email: email ?? '',
      isSuperuser: role.includes('superuser'),
      termsAccepted,
    };
  }

  #hasPersonalProfileAccess(): boolean {
    return this.me()?.hasPersonalProfileAccess !== false;
  }

  /** Clear all data after logout */
  #clearAll(): void {
    this.#profile.set(null);
    this.#profileSwitchService.clearActiveState();
    this.#initialized.set(false);
  }
}
